/**
 * Portal Landing - External Vendor Portal Entry Point
 *
 * Token-based authentication for external vendors to submit data.
 * No user session required - access via secure token link.
 *
 * Flow:
 * 1. Vendor receives email with portal link
 * 2. Token validated on page load
 * 3. Vendor sees submission instructions
 * 4. Proceeds to upload data files
 *
 * @epic DC-523 Vendor Certification Program
 * @task DC-532
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BRANDING } from '../../../config/branding';
import '../../../styles/vendor-portal.css';

// ============================================================================
// TYPES
// ============================================================================

interface PortalSession {
  assessment_id: string;
  vendor_id: string;
  vendor_name: string;
  product_id: string;
  product_name: string;
  status: string;
  deadline?: string;
  files_uploaded: number;
  notes?: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const PortalLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<PortalSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No access token provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/portal/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('This link has expired or is no longer valid. Please contact the requesting organization for a new link.');
          } else {
            setError('Unable to validate access. Please try again or contact support.');
          }
          setLoading(false);
          return;
        }

        const data = await response.json();
        setSession(data);
      } catch {
        setError('Unable to connect to the server. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const handleContinue = () => {
    navigate(`/portal/${token}/upload`);
  };

  // Open user guide in Typora/default markdown viewer
  const handleOpenGuide = async () => {
    try {
      await fetch('/open-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidePath: 'Vendor_Portal_Guide.md' })
      });
    } catch (err) {
      console.error('[PortalLanding] Error opening guide:', err);
    }
  };

  const formatDeadline = (deadline: string) => {
    const date = new Date(deadline);
    const now = new Date();
    const diffDays = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    const formatted = date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    if (diffDays < 0) {
      return { text: formatted, warning: 'overdue' };
    } else if (diffDays <= 3) {
      return { text: formatted, warning: 'urgent' };
    } else if (diffDays <= 7) {
      return { text: formatted, warning: 'soon' };
    }
    return { text: formatted, warning: null };
  };

  if (loading) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="portal-loading">
            <div className="spinner" />
            <p>Validating access...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-page">
        <div className="portal-container">
          <div className="portal-error">
            <div className="error-icon">⚠️</div>
            <h1>Access Denied</h1>
            <p>{error}</p>
            <div className="error-help">
              <p>If you believe this is an error, please contact the organization that sent you this link.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const deadlineInfo = session.deadline ? formatDeadline(session.deadline) : null;

  return (
    <div className="portal-page">
      <div className="portal-container">
        {/* Header */}
        <div className="portal-header">
          <div className="portal-logo">
            <span className="logo-icon">🧭</span>
            <span className="logo-text">{BRANDING.productName}</span>
          </div>
          <span className="portal-badge">Vendor Portal</span>
        </div>

        {/* Welcome */}
        <div className="portal-welcome">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1>Welcome, {session.vendor_name}</h1>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleOpenGuide();
              }}
              className="help-link"
              title="Open Vendor Portal User Guide"
              style={{
                fontSize: '1.25rem',
                textDecoration: 'none',
                color: '#3b82f6',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                backgroundColor: '#eff6ff',
                border: '1px solid #bfdbfe',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#dbeafe';
                e.currentTarget.style.borderColor = '#3b82f6';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#eff6ff';
                e.currentTarget.style.borderColor = '#bfdbfe';
              }}
            >
              ?
            </a>
          </div>
          <p>
            You've been invited to submit data for FAIR certification assessment
            for your product <strong>{session.product_name}</strong>.
          </p>
        </div>

        {/* Deadline Alert */}
        {deadlineInfo && (
          <div className={`deadline-alert ${deadlineInfo.warning || ''}`}>
            <span className="deadline-icon">📅</span>
            <div className="deadline-info">
              <span className="deadline-label">Submission Deadline</span>
              <span className="deadline-date">{deadlineInfo.text}</span>
            </div>
          </div>
        )}

        {/* Progress */}
        {session.files_uploaded > 0 && (
          <div className="progress-banner">
            <span className="progress-icon">📁</span>
            <span>{session.files_uploaded} file(s) already uploaded</span>
            <button className="btn btn-link" onClick={handleContinue}>
              Continue where you left off →
            </button>
          </div>
        )}

        {/* Instructions */}
        <div className="portal-instructions">
          <h2>What You'll Need</h2>

          <div className="instruction-cards">
            <div className="instruction-card">
              <div className="card-icon">📊</div>
              <h3>Sample Data Files</h3>
              <p>
                Upload representative data files from your product.
                Supported formats: CSV, TSV, XLSX, JSON, Parquet.
              </p>
              <span className="requirement">Required</span>
            </div>

            <div className="instruction-card">
              <div className="card-icon">🔗</div>
              <h3>ID Crosswalk File</h3>
              <p>
                If your data uses proprietary identifiers, provide a mapping
                to public ontologies (HGNC, UniProt, ChEBI, etc.).
              </p>
              <span className="requirement optional">Optional</span>
            </div>

            <div className="instruction-card">
              <div className="card-icon">📝</div>
              <h3>Documentation</h3>
              <p>
                Any relevant documentation about your data structure,
                field definitions, or data collection methods.
              </p>
              <span className="requirement optional">Optional</span>
            </div>
          </div>
        </div>

        {/* Assessment Info */}
        <div className="assessment-info">
          <h2>Assessment Process</h2>
          <p>Your data will be evaluated across four dimensions:</p>

          <div className="assessment-stations">
            <div className="station-item">
              <span className="station-number">1</span>
              <div className="station-content">
                <h4>Data Quality</h4>
                <p>Completeness, accuracy, consistency, and outlier detection</p>
              </div>
            </div>
            <div className="station-item">
              <span className="station-number">2</span>
              <div className="station-content">
                <h4>ML Readiness</h4>
                <p>Suitability for machine learning applications</p>
              </div>
            </div>
            <div className="station-item">
              <span className="station-number">3</span>
              <div className="station-content">
                <h4>Ontology Alignment</h4>
                <p>Mapping to standard vocabularies and ontologies</p>
              </div>
            </div>
            <div className="station-item">
              <span className="station-number">4</span>
              <div className="station-content">
                <h4>FAIR Compliance</h4>
                <p>Findability, Accessibility, Interoperability, Reusability</p>
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="privacy-notice">
          <h3>Data Privacy</h3>
          <p>
            Your data is processed securely and used only for assessment purposes.
            We do not share your data with third parties. All uploads are encrypted
            in transit and at rest.
          </p>
        </div>

        {/* Action */}
        <div className="portal-actions">
          <button className="btn btn-primary btn-lg" onClick={handleContinue}>
            {session.files_uploaded > 0 ? 'Continue Submission' : 'Start Submission'}
          </button>
        </div>

        {/* Footer */}
        <div className="portal-footer">
          <p>
            Questions? Contact the organization that sent you this link for assistance.
          </p>
          <p className="footer-meta">
            Assessment ID: {session.assessment_id.substring(0, 8)}...
          </p>
        </div>
      </div>
    </div>
  );
};

export default PortalLanding;
